import useSWR from 'swr';
import container from 'src/container';
import { Message } from 'src/domain/message';
import { PaginatedResponse } from 'src/domain/pagination';

const {
    cradle: { munduClient },
} = container;

const MESSAGE_REFRESH_INTERVAL_MS = 30 * 1000;

const getVisibleRefreshInterval = () => {
    if (typeof document === 'undefined') {
        return MESSAGE_REFRESH_INTERVAL_MS;
    }

    return document.visibilityState === 'visible'
        ? MESSAGE_REFRESH_INTERVAL_MS
        : 0;
};

type UseMessagesParams = {
    organizationId?: string;
    consumerId?: string;
    nextOfKinId?: string;
    allowedGroupIds?: string[];
    strict?: boolean;
    commentsEnabled?: boolean;
    readStatus?: 'all' | 'unread';
    sentByMe?: boolean;
    directToConsumer?: boolean;
    sendToAll?: boolean;
    replyStatus?: 'noStaffReply';
    month?: string;
    limit?: number;
    pageIndex?: number;
};

type MessagesKey = readonly [
    'messages',
    string,
    string | null,
    string | null,
    string | null,
    boolean | null,
    string | null,
    string | null,
    boolean | null,
    boolean | null,
    boolean | null,
    string | null,
    string | null,
    number | null,
    number | null,
];

export const useMessages = ({
    organizationId,
    consumerId,
    nextOfKinId,
    allowedGroupIds,
    strict,
    commentsEnabled,
    readStatus,
    sentByMe,
    directToConsumer,
    sendToAll,
    replyStatus,
    month,
    limit,
    pageIndex,
}: UseMessagesParams) => {
    const allowedGroupIdsKey = allowedGroupIds?.join(',') ?? null;
    const commentsEnabledKey =
        typeof commentsEnabled === 'boolean' ? String(commentsEnabled) : null;
    const shouldAutoRefresh = readStatus !== 'unread';

    const swrKey: MessagesKey | null = organizationId
        ? [
              'messages',
              organizationId,
              consumerId ?? null,
              nextOfKinId ?? null,
              allowedGroupIdsKey,
              strict ?? null,
              commentsEnabledKey,
              readStatus ?? null,
              sentByMe ?? null,
              directToConsumer ?? null,
              sendToAll ?? null,
              replyStatus ?? null,
              month ?? null,
              limit ?? null,
              pageIndex ?? null,
          ]
        : null;

    const { data, error, mutate, isValidating } = useSWR<
        PaginatedResponse<Message> | undefined,
        Error,
        MessagesKey | null
    >(
        swrKey,
        async (
            _key: 'messages',
            orgId: string,
            consumerKey: string | null,
            nextOfKinKey: string | null,
            allowedGroupIdsList: string | null,
            strictKey: boolean | null,
            commentsEnabledValue: string | null,
            readStatusKey: string | null,
            sentByMeKey: boolean | null,
            directToConsumerKey: boolean | null,
            sendToAllKey: boolean | null,
            replyStatusKey: string | null,
            monthKey: string | null,
            limitKey: number | null,
            pageKey: number | null,
        ) =>
            munduClient.listMessages({
                organizationId: orgId,
                consumerId: consumerKey ?? undefined,
                nextOfKinId: nextOfKinKey ?? undefined,
                allowedGroupIds: allowedGroupIdsList
                    ? allowedGroupIdsList
                          .split(',')
                          .filter(groupId => groupId.length > 0)
                    : undefined,
                strict: strictKey ?? undefined,
                commentsEnabled:
                    commentsEnabledValue === null
                        ? undefined
                        : commentsEnabledValue === 'true',
                readStatus: readStatusKey === 'unread' ? 'unread' : undefined,
                sentByMe: sentByMeKey ?? undefined,
                directToConsumer: directToConsumerKey ?? undefined,
                sendToAll: sendToAllKey ?? undefined,
                replyStatus:
                    replyStatusKey === 'noStaffReply'
                        ? 'noStaffReply'
                        : undefined,
                month: monthKey ?? undefined,
                limit: limitKey ?? undefined,
                pageIndex: pageKey ?? undefined,
            }),
        {
            revalidateOnFocus: shouldAutoRefresh,
            revalidateOnReconnect: shouldAutoRefresh,
            refreshWhenHidden: false,
            refreshInterval: shouldAutoRefresh ? getVisibleRefreshInterval : 0,
        },
    );

    return {
        messages: data?.data ?? [],
        pageInfo: data?.info,
        isLoading: Boolean(organizationId) && !error && !data,
        isError: error,
        mutate,
        isValidating,
    };
};
